import { useState } from "react";
import { useSearchParams } from "react-router";
import { useForm } from "@tanstack/react-form";
import { Button, Icon } from "@sellar-io/components";
import {
  useDeliveryPreferencesStore,
  type DeliveryTime,
} from "../deliveryPreferencesStore";
import { deliveryTimeSchema } from "../stepRegistry";
import { OrderBanner } from "../components/OrderBanner";
import { OrderSummaryDrawer } from "../components/OrderSummaryDrawer";
import { useOrderLinkFlowOrder } from "../../../../../hooks/api/v2/orderLinkFlow/useOrderLinkFlowOrder";

const DELIVERY_OPTIONS: {
  value: DeliveryTime;
  label: string;
  description: string;
}[] = [
  {
    value: "AM",
    label: "AM",
    description: "",
  },
  {
    value: "EARLY_PM",
    label: "Early PM",
    description: "",
  },
  {
    value: "FLEXIBLE",
    label: "Flexible",
    description: "",
  },
];

export default function DeliveryTimeStep() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: order } = useOrderLinkFlowOrder(token);

  const store = useDeliveryPreferencesStore();
  const form = useForm({
    defaultValues: {
      deliveryTime: store.deliveryTime ?? null,
    },
    validators: {
      onSubmit: deliveryTimeSchema,
    },
    onSubmit: ({ value }) => {
      if (!value.deliveryTime) return;
      store.setDeliveryTime(value.deliveryTime);
      store.next();
    },
  });

  return (
    <div className="flex min-h-screen flex-col">
      {order && (
        <OrderBanner order={order} onShowDetails={() => setDrawerOpen(true)} />
      )}

      <div className="w-full flex-1 border-t border-zinc-200 bg-neutral-100 px-5 py-8">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-5">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="mb-3 flex cursor-pointer items-center gap-2 text-zinc-800 hover:text-zinc-600"
            >
              <Icon icon="CHEVRON_LEFT" size={0.9} />
              <h2 className="text-xl font-semibold">
                When should this order arrive?
              </h2>
            </button>
            <p className="mt-1 text-sm text-zinc-500">
              Set your delivery time preferences for this and any future orders
              on Sellar. You can change this at any time
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="flex flex-col gap-3"
          >
            <form.Field name="deliveryTime">
              {(field) => (
                <div className="flex flex-col gap-2">
                  {DELIVERY_OPTIONS.map((option) => {
                    const selected = field.state.value === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => field.handleChange(option.value)}
                        className={`cursor-pointer rounded-xl border bg-white p-4 text-left transition-colors ${
                          selected
                            ? "border-zinc-800"
                            : "border-zinc-200 hover:border-zinc-400"
                        }`}
                      >
                        <p className="text-sm font-semibold text-zinc-800">
                          {option.label}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {option.description}
                        </p>
                      </button>
                    );
                  })}
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-red-500">
                      {field.state.meta.errors
                        .filter(Boolean)
                        .map((e) => (typeof e === "string" ? e : e.message))
                        .join(", ")}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <Button type="submit" color="primary" className="w-full">
              Continue
            </Button>
            <p className="text-center text-xs text-zinc-500">
              You need to continue to save your preference
            </p>
          </form>
        </div>
      </div>

      {order && (
        <OrderSummaryDrawer
          active={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          order={order}
        />
      )}
    </div>
  );
}
